/**
 * @fileoverview UserAccountMenu — popover *content* rendered when the
 * top-right `PrincipalAvatar` in the Shell is clicked. Layout: a
 * `UserInfoHeader` at the top (avatar + name link + (username)), followed
 * by a Blueprint `<Menu>` of `UserAccountMenuItem` rows. Consumers compose
 * the body (Settings, divider, TrackSwitcherButton, divider, Log out, etc.)
 * and wrap this content in a Blueprint `<Popover position="right">` from
 * the avatar button.
 *
 * Foundry mapping: `user-account-menu__user-menu__*` containing
 * `__user-info__*` + a `bp6-menu` stack with `bp6-menu-divider` separators.
 */

import { Menu } from "@blueprintjs/core";
import type { MouseEvent, ReactElement, ReactNode } from "react";
import { UserInfoHeader } from "./user-info-header";

interface UserAccountMenuProps {
  /** Display name shown as the primary link in the header row. */
  name: string;
  /** Email or login handle shown beneath the name. */
  username?: string;
  /** Initials used by the inline `PrincipalAvatar` (you-avatar variant). */
  initials: string;
  /** Optional user avatar image. */
  imageUrl?: string;
  /** Settings/profile destination for the name link. */
  profileHref?: string;
  /** Click handler for the name link. Receives the event so SPA consumers
   *  can `preventDefault()` and route client-side. */
  onProfileClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  /** Menu body — Blueprint `<MenuItem>` / `<MenuDivider>` / `UserAccountMenuItem` rows. */
  children: ReactNode;
  className?: string;
}

export function UserAccountMenu({
  name,
  username,
  initials,
  imageUrl,
  profileHref,
  onProfileClick,
  children,
  className,
}: UserAccountMenuProps): ReactElement {
  return (
    <div data-testid="user-account-menu" className={["bp-user-account-menu", className].filter(Boolean).join(" ")}>
      <UserInfoHeader
        name={name}
        username={username}
        initials={initials}
        imageUrl={imageUrl}
        href={profileHref}
        onNameClick={onProfileClick}
      />
      <Menu className="bp-user-account-menu-list">{children}</Menu>
    </div>
  );
}
